import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { logout, loginUser, updateUserSettings, patchUser } from '../redux/authSlice';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { user, status, error } = useSelector((state) => state.auth, (left, right) => {
    // Custom equality function to prevent unnecessary re-renders
    return left.user?.id === right.user?.id &&
           left.user?.phone_verified_at === right.user?.phone_verified_at &&
           left.user?.discord_verified_at === right.user?.discord_verified_at &&
           left.user?.phone === right.user?.phone &&
           left.user?.discord_username === right.user?.discord_username &&
           left.user?.onboarding_tour_status === right.user?.onboarding_tour_status &&
           left.status === right.status &&
           left.error === right.error;
  });

  // Log state changes in development only
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Only log when there are meaningful changes (not on every render)
      console.log('AuthContext state changed:', {
        hasUser: !!user,
        user: user ? { id: user.id, username: user.username } : null, // Only log essential user info
        status,
        error
      });
    }
  }, [user, status, error]);

  const handleLogout = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const handleLogin = useCallback(async (credentials) => {
    try {
      const result = await dispatch(loginUser(credentials)).unwrap();
      return result;
    } catch (err) {
      console.error('Login failed:', err);
      throw err;
    }
  }, [dispatch]);

  const handleUpdateUser = useCallback(async (updates) => {
    try {
      const result = await dispatch(updateUserSettings(updates)).unwrap();
      return result;
    } catch (err) {
      console.error('User update failed:', err);
      throw err;
    }
  }, [dispatch]);

  const mergeUser = useCallback((updates) => {
    dispatch(patchUser(updates));
  }, [dispatch]);

  const value = useMemo(() => ({
    user,
    status,
    error,
    logout: handleLogout,
    login: handleLogin,
    updateUser: handleUpdateUser,
    mergeUser,
    isAuthenticated: !!user
  }), [user, status, error, handleLogout, handleLogin, handleUpdateUser, mergeUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
